import { createMemo, createSignal, For, type JSX, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { msg } from "@lingui/core/macro";
import {
  Grid2x2,
  Heart,
  Image as ImageIcon,
  Package,
  Settings,
  Sparkles,
  Star,
  Tag,
} from "lucide-solid";

import { Link } from "@/lib/u-router";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { createPowerSyncQuery } from "@famidex/shared/solid";
import { count, eq, follow } from "@famidex/powersync";
import {
  deriveCollectorTitle,
  FRAME_IDS,
  type FrameId,
  getCollectionQuery,
  getCollectorProfileQuery,
  getFavoritesQuery,
  getFeaturedQuery,
  getTagsQuery,
  queryKeys,
  setFrameMutation,
} from "@famidex/shared";
import {
  Tabs,
  TabsIndicator,
  TabsList,
  TabsPanel,
  TabsPanels,
  TabsTrigger,
} from "@/components/ui/tabs";
import { avatarUrl as buildAvatarUrl } from "@famidex/shared";
import { SERVER_URL } from "@/lib/runtime-config";
import { SeasonalFrame } from "./seasonal-frame";

const TAB_ORDER = ["collection", "posts", "wishes", "favorites", "tags"] as const;

/**
 * 🧑‍🎨 PERFIL DEL COLECCIONISTA — pantalla del módulo perfil (tab /journal).
 * Traducción de los diseños Stitch `perfil_del_coleccionista` (+ destacados,
 * favoritos/etiquetas, publicaciones, marco de otoño). Muestra el perfil del
 * usuario ACTUAL (self-view): destacados (máx. 5), stats sociales, marco
 * estacional y pestañas. Consume la colección/gamificación por LECTURA; el
 * modelo propio (perfil/destacados/favoritos/etiquetas) vive en el stream
 * privado `my_profile`. La privacidad se configura en /journal/privacy.
 */
export function CollectorProfilePage() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { t } = useI18n();
  const { userId } = session;

  const frameLabel = (id: FrameId): string => {
    switch (id) {
      case "autumn": {
        return t(msg`Otoño`);
      }
      case "spring": {
        return t(msg`Primavera`);
      }
      case "winter": {
        return t(msg`Invierno`);
      }
      case "summer": {
        return t(msg`Verano`);
      }
      default: {
        return "";
      }
    }
  };

  const [tab, setTab] = createSignal<(typeof TAB_ORDER)[number]>("collection");
  const enabled = () => Boolean(drizzle() && userId());

  const profile = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.collectorProfile.settings(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid ? getCollectorProfileQuery(d, uid) : null;
    },
  });

  const collection = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.collection.items(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid ? getCollectionQuery(d, uid) : null;
    },
  });

  const featured = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.collectorProfile.featured(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid ? getFeaturedQuery(d, uid) : null;
    },
  });

  const favorites = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.collectorProfile.favorites(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid ? getFavoritesQuery(d, uid) : null;
    },
  });

  const tags = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.collectorProfile.tags(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid ? getTagsQuery(d, uid) : null;
    },
  });

  // Stats sociales: seguidos/seguidores desde el grafo social (Agente D), sólo
  // lectura. Las aristas ya están en la DB local (stream `social_follows`).
  const following = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.collectorProfile.following(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid
        ? d.select({ n: count() }).from(follow).where(eq(follow.follower_id, uid))
        : null;
    },
  });
  const followers = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.collectorProfile.followers(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid
        ? d.select({ n: count() }).from(follow).where(eq(follow.followee_id, uid))
        : null;
    },
  });

  const settings = () => profile.data?.[0];

  const liveCollection = createMemo(() => collection.data ?? []);
  const ownedItems = createMemo(() => liveCollection().filter((i) => i.state !== "wishlist"));
  const wishItems = createMemo(() => liveCollection().filter((i) => i.state === "wishlist"));

  const figureCount = createMemo(() => ownedItems().reduce((sum, i) => sum + (i.quantity ?? 1), 0));
  const rankTitle = createMemo(() => deriveCollectorTitle(figureCount()));

  const displayName = () =>
    settings()?.display_name || session.data?.user.name || t(msg`Coleccionista`);
  // `user.image` guarda una CLAVE de storage (`avatars/{userId}/{hash}.webp`),
  // no una URL: la fila tiene que ser portable entre dev/rc/prod, que tienen
  // distinto origen de API. Pasar la clave cruda a un `<img src>` la resolvería
  // como ruta RELATIVA del front y daría un 404 silencioso (foto rota), así que
  // la URL absoluta la construye `avatarUrl` con el origen del runtime-config.
  const avatarSrc = () => buildAvatarUrl(session.data?.user.image, SERVER_URL);

  const setFrame = (frameId: FrameId | null) => {
    const d = drizzle();
    const uid = userId();
    if (!d || !uid) {
      return;
    }
    const next = settings()?.frame_id === frameId ? null : frameId;
    void setFrameMutation(d, uid, next);
  };

  return (
    <div class="flex flex-col gap-6" data-testid="collector-profile-page">
      {/* ── Cabecera ─────────────────────────────────────────────────────── */}
      <header class="flex flex-col items-center gap-3 text-center">
        <SeasonalFrame
          frameId={settings()?.frame_id}
          imageUrl={avatarSrc()}
          alt={displayName()}
          size={112}
          badge={
            <span class="whitespace-nowrap rounded-full bg-accent px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-accent-foreground shadow-sm">
              {rankTitle()}
            </span>
          }
        />
        <h1 class="mt-2 text-2xl font-bold text-foreground">{displayName()}</h1>

        <div class="flex items-center gap-2">
          <Link
            to="/profile"
            class="rounded-full border border-border bg-card px-4 py-1.5 text-sm font-semibold text-foreground transition-colors hover:bg-accent/10"
          >
            {t(msg`Editar perfil`)}
          </Link>
          <Link
            to="/journal/privacy"
            data-testid="open-privacy"
            class="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground"
          >
            <Settings class="h-4 w-4" />
            {t(msg`Privacidad`)}
          </Link>
        </div>

        <Show when={settings()?.bio}>
          <p class="max-w-md text-sm italic leading-relaxed text-muted-foreground">
            {settings()?.bio}
          </p>
        </Show>

        {/* Stats sociales */}
        <dl class="mt-1 flex items-stretch gap-6">
          <Stat label={t(msg`Figuras`)} value={figureCount()} testid="stat-figures" />
          <StatDivider />
          <Stat
            label={t(msg`Seguidores`)}
            value={followers.data?.[0]?.n ?? 0}
            testid="stat-followers"
          />
          <StatDivider />
          <Stat
            label={t(msg`Siguiendo`)}
            value={following.data?.[0]?.n ?? 0}
            testid="stat-following"
          />
        </dl>
      </header>

      {/* ── Marco estacional (selección) ─────────────────────────────────── */}
      <section class="rounded-xl border border-border/60 bg-card/60 p-4">
        <h2 class="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
          <Sparkles class="h-3.5 w-3.5 text-primary" />
          {t(msg`Marco estacional`)}
        </h2>
        <div class="flex flex-wrap gap-2" data-testid="frame-picker">
          <For each={FRAME_IDS}>
            {(id) => (
              <button
                type="button"
                data-testid={`frame-${id}`}
                aria-pressed={settings()?.frame_id === id}
                onClick={() => setFrame(id)}
                class="rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors data-[active=true]:border-primary data-[active=true]:bg-primary data-[active=true]:text-primary-foreground"
                classList={{
                  "border-primary bg-primary text-primary-foreground": settings()?.frame_id === id,
                  "border-border bg-background text-foreground hover:bg-accent/10":
                    settings()?.frame_id !== id,
                }}
              >
                {frameLabel(id)}
              </button>
            )}
          </For>
        </div>
      </section>

      {/* ── Destacados (máx. 5) ──────────────────────────────────────────── */}
      <Show when={(featured.data ?? []).length > 0}>
        <section>
          <h2 class="mb-3 flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-muted-foreground">
            <Star class="h-4 w-4 text-primary" />
            {t(msg`Figuras destacadas`)}
          </h2>
          <ul class="-mx-1 flex gap-3 overflow-x-auto px-1 pb-1" data-testid="featured-strip">
            <For each={featured.data}>
              {(f) => (
                <li
                  data-testid="featured-item"
                  class="w-36 shrink-0 overflow-hidden rounded-xl border border-border/60 bg-card"
                >
                  <div class="relative aspect-square bg-muted">
                    <Show
                      when={f.image_url}
                      fallback={
                        <div class="grid h-full w-full place-items-center text-muted-foreground/50">
                          <Package class="h-7 w-7" />
                        </div>
                      }
                    >
                      <img
                        src={f.image_url ?? ""}
                        alt={f.edition_name ?? ""}
                        loading="lazy"
                        class="h-full w-full object-cover"
                      />
                    </Show>
                    <Show when={f.is_limited}>
                      <span class="absolute left-2 top-2 rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold uppercase text-accent-foreground">
                        {t(msg`Rara`)}
                      </span>
                    </Show>
                  </div>
                  <p class="line-clamp-2 p-2.5 text-xs font-semibold leading-tight text-foreground">
                    {f.edition_name ?? t(msg`Producto`)}
                  </p>
                </li>
              )}
            </For>
          </ul>
        </section>
      </Show>

      {/* ── Pestañas ─────────────────────────────────────────────────────── */}
      <Tabs
        value={tab()}
        onChange={(v) => {
          const next = TAB_ORDER.find((candidate) => candidate === v);
          if (next) {
            setTab(next);
          }
        }}
      >
        <TabsList>
          {/* Label visible en la pestaña ACTIVA (diseño Stitch rotula la activa) +
              siempre en ≥sm; a 390px con 5 tabs mostrar las 5 rotularía en overflow. */}
          <TabsTrigger value="collection" data-testid="tab-collection">
            <Grid2x2 class="h-4 w-4" />
            <span
              classList={{
                "sm:inline": true,
                inline: tab() === "collection",
                hidden: tab() !== "collection",
              }}
            >
              {t(msg`Colección`)}
            </span>
          </TabsTrigger>
          <TabsTrigger value="posts" data-testid="tab-posts">
            <ImageIcon class="h-4 w-4" />
            <span
              classList={{
                "sm:inline": true,
                inline: tab() === "posts",
                hidden: tab() !== "posts",
              }}
            >
              {t(msg`Publicaciones`)}
            </span>
          </TabsTrigger>
          <TabsTrigger value="wishes" data-testid="tab-wishes">
            <Sparkles class="h-4 w-4" />
            <span
              classList={{
                "sm:inline": true,
                inline: tab() === "wishes",
                hidden: tab() !== "wishes",
              }}
            >
              {t(msg`Deseos`)}
            </span>
          </TabsTrigger>
          <TabsTrigger value="favorites" data-testid="tab-favorites">
            <Heart class="h-4 w-4" />
            <span
              classList={{
                "sm:inline": true,
                inline: tab() === "favorites",
                hidden: tab() !== "favorites",
              }}
            >
              {t(msg`Favoritos`)}
            </span>
          </TabsTrigger>
          <TabsTrigger value="tags" data-testid="tab-tags">
            <Tag class="h-4 w-4" />
            <span
              classList={{ "sm:inline": true, inline: tab() === "tags", hidden: tab() !== "tags" }}
            >
              {t(msg`Etiquetas`)}
            </span>
          </TabsTrigger>
          <TabsIndicator />
        </TabsList>

        <TabsPanels value={tab()} order={TAB_ORDER} class="pt-4">
          <TabsPanel value="collection">
            <ProductGrid
              items={ownedItems().map((i) => ({
                key: i.id,
                image: i.image_url,
                name: i.edition_name,
              }))}
              emptyIcon={Package}
              emptyText={t(msg`Aún no has añadido figuras a tu colección.`)}
              testid="profile-collection-grid"
            />
          </TabsPanel>

          <TabsPanel value="posts">
            <EmptyPanel
              icon={ImageIcon}
              text={t(msg`Aún no has publicado nada. Comparte tu primera foto desde la comunidad.`)}
              testid="profile-posts-empty"
            />
          </TabsPanel>

          <TabsPanel value="wishes">
            <ProductGrid
              items={wishItems().map((i) => ({
                key: i.id,
                image: i.image_url,
                name: i.edition_name,
              }))}
              emptyIcon={Sparkles}
              emptyText={t(msg`Tu lista de deseos está vacía.`)}
              testid="profile-wishes-grid"
            />
          </TabsPanel>

          <TabsPanel value="favorites">
            <ProductGrid
              items={(favorites.data ?? []).map((f) => ({
                key: f.id,
                image: f.image_url,
                name: f.edition_name,
              }))}
              emptyIcon={Heart}
              emptyText={t(msg`Marca tus figuras favoritas para verlas aquí.`)}
              testid="profile-favorites-grid"
            />
          </TabsPanel>

          <TabsPanel value="tags">
            <Show
              when={(tags.data ?? []).length > 0}
              fallback={
                <EmptyPanel
                  icon={Tag}
                  text={t(msg`Aún no has etiquetado ninguna figura.`)}
                  testid="profile-tags-empty"
                />
              }
            >
              <ul class="flex flex-col gap-2" data-testid="profile-tags-list">
                <For each={tags.data}>
                  {(tg) => (
                    <li class="flex items-center justify-between rounded-lg border border-border/50 bg-card/50 px-3 py-2">
                      <span class="text-sm font-medium text-foreground">
                        {tg.edition_name ?? t(msg`Producto`)}
                      </span>
                      <span class="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-0.5 text-[11px] font-bold text-accent-foreground">
                        <Tag class="h-3 w-3" />
                        {tg.label}
                      </span>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </TabsPanel>
        </TabsPanels>
      </Tabs>
    </div>
  );
}

function Stat(props: { label: string; value: number; testid?: string }) {
  return (
    <div class="flex flex-col items-center" data-testid={props.testid}>
      <dd class="text-xl font-bold text-foreground">{props.value}</dd>
      <dt class="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {props.label}
      </dt>
    </div>
  );
}

function StatDivider() {
  return <div class="w-px self-stretch bg-border/70" aria-hidden="true" />;
}

interface GridItem {
  key: string;
  image?: string | null;
  name?: string | null;
}

function ProductGrid(props: {
  items: GridItem[];
  emptyIcon: (p: { class?: string }) => JSX.Element;
  emptyText: string;
  testid?: string;
}) {
  return (
    <Show
      when={props.items.length > 0}
      fallback={
        <EmptyPanel
          icon={props.emptyIcon}
          text={props.emptyText}
          testid={`${props.testid}-empty`}
        />
      }
    >
      <ul class="grid grid-cols-3 gap-2" data-testid={props.testid}>
        <For each={props.items}>
          {(item) => (
            <li
              data-testid="profile-grid-item"
              class="relative aspect-square overflow-hidden rounded-lg border border-border/50 bg-muted"
            >
              <Show
                when={item.image}
                fallback={
                  <div class="grid h-full w-full place-items-center text-muted-foreground/40">
                    <Package class="h-7 w-7" />
                  </div>
                }
              >
                <img
                  src={item.image ?? ""}
                  alt={item.name ?? ""}
                  loading="lazy"
                  class="h-full w-full object-cover"
                />
              </Show>
            </li>
          )}
        </For>
      </ul>
    </Show>
  );
}

function EmptyPanel(props: {
  icon: (p: { class?: string }) => JSX.Element;
  text: string;
  testid?: string;
}) {
  return (
    <div
      data-testid={props.testid}
      class="grid place-items-center gap-2 rounded-xl border border-dashed border-border/70 bg-card/40 py-14 text-center text-muted-foreground"
    >
      <Dynamic component={props.icon} class="h-8 w-8" />
      <p class="max-w-xs text-sm">{props.text}</p>
    </div>
  );
}
